import React, { useEffect, useState, useMemo } from 'react';
import ElearningCourseCard from '../ElearningCourseCard';
import { MODULES_CATALOG } from '../../data/modulesCatalog';
import { useAuth } from '../../contexts/AuthContext';
import './ModuleList.css';
import './Dashboard.css';

const ModuleList = ({ user }) => {
  const [filter, setFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const { moduleProgress, refreshModuleProgress } = useAuth();

  useEffect(() => {
    void refreshModuleProgress?.();
  }, [refreshModuleProgress]);

  const modules = useMemo(
    () =>
      MODULES_CATALOG.map((m) => {
        const progressDoc = moduleProgress?.find((p) => p.moduleId === m.id);
        if (!progressDoc) return m;
        return {
          ...m,
          status: progressDoc.status || m.status,
          isEnrolled: Boolean(progressDoc.isEnrolled || progressDoc.status === 'in-progress' || progressDoc.status === 'completed'),
          isCompleted: Boolean(progressDoc.isCompleted || progressDoc.status === 'completed'),
          progress: Number(progressDoc.progressPercent) || 0,
          currentStageId: progressDoc.currentStageId || null,
          completedStagesCount: progressDoc.completedStagesCount || 0,
          totalStages: progressDoc.totalStages || 0,
        };
      }),
    [moduleProgress]
  );

  const filteredModules = useMemo(() => {
    return modules.filter((module) => {
      const matchesSearch =
        !searchQuery.trim() ||
        module.title.toLowerCase().includes(searchQuery.toLowerCase());
      if (!matchesSearch) return false;
      if (filter === 'all') return true;
      if (filter === 'enrolled') return module.isEnrolled || module.status === 'in-progress' || module.status === 'completed' || module.progress > 0;
      if (filter === 'completed') return module.status === 'completed';
      if (filter === 'not-started') return (module.status || 'not-started') === 'not-started' && (module.progress || 0) === 0;
      return true;
    });
  }, [modules, filter, searchQuery]);

  const stats = {
    total: modules.length,
    enrolled: modules.filter((m) => m.isEnrolled || m.status === 'in-progress' || m.status === 'completed' || (m.progress || 0) > 0).length,
    completed: modules.filter((m) => m.status === 'completed').length,
    inProgress: modules.filter((m) => m.status === 'in-progress').length,
  };

  return (
    <div className="module-list-page">
      <div className="module-list-container">
        {/* Header Section - unchanged layout */}
        <div className="page-header">
          <div className="header-top">
            <div>
              <h1 className="page-title">My Learning Modules</h1>
              <p className="page-subtitle">
                Explore and continue your learning journey with our comprehensive modules
              </p>
            </div>
            <div className="header-search">
              <svg className="search-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <circle cx="11" cy="11" r="8" />
                <path d="m21 21-4.35-4.35" />
              </svg>
              <input
                type="search"
                placeholder="Search modules..."
                className="search-input"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                aria-label="Search modules"
              />
            </div>
          </div>

          <div className="header-stats">
            <div className="header-stat">
              <span className="stat-number">{stats.total}</span>
              <span className="stat-text">Total Modules</span>
            </div>
            <div className="header-stat">
              <span className="stat-number">{stats.enrolled}</span>
              <span className="stat-text">Enrolled</span>
            </div>
            <div className="header-stat">
              <span className="stat-number">{stats.inProgress}</span>
              <span className="stat-text">In Progress</span>
            </div>
            <div className="header-stat">
              <span className="stat-number">{stats.completed}</span>
              <span className="stat-text">Completed</span>
            </div>
          </div>
        </div>

        {/* Filter Section */}
        <div className="filter-section">
          <div className="filter-buttons">
            <button
              type="button"
              className={`filter-btn ${filter === 'all' ? 'active' : ''}`}
              onClick={() => setFilter('all')}
            >
              All Modules
            </button>
            <button
              type="button"
              className={`filter-btn ${filter === 'enrolled' ? 'active' : ''}`}
              onClick={() => setFilter('enrolled')}
            >
              Enrolled
            </button>
            <button
              type="button"
              className={`filter-btn ${filter === 'completed' ? 'active' : ''}`}
              onClick={() => setFilter('completed')}
            >
              Completed
            </button>
            <button
              type="button"
              className={`filter-btn ${filter === 'not-started' ? 'active' : ''}`}
              onClick={() => setFilter('not-started')}
            >
              Not Started
            </button>
          </div>
        </div>

        {/* Same card design as Learning Hub home */}
        {filteredModules.length > 0 ? (
          <div className="elearning-grid module-list-elearning-grid">
            {filteredModules.map((module, index) => (
              <ElearningCourseCard key={module.id} module={module} imagePriority={index === 0} />
            ))}
          </div>
        ) : (
          <div className="empty-state">
            <div className="empty-icon">📚</div>
            <h3 className="empty-title">No modules found</h3>
            <p className="empty-description">
              Try adjusting your filters or search to see more modules
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ModuleList;
